import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  TCreateShiftRequest,
  TCreateShiftResponse,
  TDeleteShiftRequest,
  TDeleteShiftResponse,
  TGetAllShiftsDataRequest,
  TGetAllShiftsDataResponse,
  TGetDefaultShiftRequest,
  TGetDefaultShiftResponse,
  TGetShiftByIdRequest,
  TGetShiftByIdResponse,
  TSetDefaultShiftRequest,
  TSetDefaultShiftResponse,
  TToggleShiftActiveRequest,
  TToggleShiftActiveResponse,
  TUpdateShiftRequest,
  TUpdateShiftResponse,
} from "@/types/(company)/shift.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";

export const companyShiftApi = createApi({
  reducerPath: "companyShiftApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getAllShifts", "getShiftById"],
  endpoints: (builder) => ({
    getAllShifts: builder.query<
      TGetAllShiftsDataResponse,
      TGetAllShiftsDataRequest
    >({
      query: () => {
        return {
          url: "/company/settings/shifts",
          method: "GET",
        };
      },
      providesTags: ["getAllShifts"],
    }),
    getShiftById: builder.query<TGetShiftByIdResponse, TGetShiftByIdRequest>({
      query: ({ id }) => {
        return {
          url: "/company/settings/shifts/" + id,
          method: "GET",
        };
      },
      providesTags: ["getShiftById"],
    }),
    createShift: builder.mutation<TCreateShiftResponse, TCreateShiftRequest>({
      query: (data) => {
        return {
          url: "/company/settings/shifts",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["getAllShifts", "getShiftById"],
    }),
    updateShift: builder.mutation<TUpdateShiftResponse, TUpdateShiftRequest>({
      query: (data) => {
        const { id, ...body } = data;
        if ("id" in body) {
          delete (body as any).id;
        }
        return {
          url: "/company/settings/shifts/" + id,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["getAllShifts", "getShiftById"],
    }),
    deleteShift: builder.mutation<TDeleteShiftResponse, TDeleteShiftRequest>({
      query: (id) => ({
        url: "/company/settings/shifts/" + id,
        method: "DELETE",
      }),
      invalidatesTags: ["getAllShifts"],
    }),
    toggleShiftActive: builder.mutation<TToggleShiftActiveResponse, TToggleShiftActiveRequest>({
      query: (id) => ({
        url: `/company/settings/shifts/${id}/toggle-active`,
        method: "PATCH",
      }),
      invalidatesTags: ["getAllShifts"],
    }),
    setDefaultShift: builder.mutation<TSetDefaultShiftResponse, TSetDefaultShiftRequest>({
      query: (id) => ({
        url: `/company/settings/shifts/${id}/set-default`,
        method: "PATCH",
      }),
      invalidatesTags: ["getAllShifts"],
    }),
    getDefaultShift: builder.query<TGetDefaultShiftResponse, TGetDefaultShiftRequest>({
      query: () => ({
        url: "/company/settings/shifts/default",
        method: "GET",
      }),
      providesTags: ["getAllShifts"],
    }),
  }),
});

export const {
  useGetAllShiftsQuery,
  useGetShiftByIdQuery,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
  useToggleShiftActiveMutation,
  useSetDefaultShiftMutation,
  useGetDefaultShiftQuery,
} = companyShiftApi;
